import { Component, ElementRef, ViewChild, signal, computed, effect } from '@angular/core';
import { CommonModule } from '@angular/common';

interface Position {
  x: number;
  y: number;
}

interface Direction {
  x: number;
  y: number;
}

interface PowerUp {
  x: number;
  y: number;
  type: 'speed_boost';
  active: boolean;
  spawnTime: number;
  timeLeft: number;
}

enum ControlMode {
  SWIPE = 'swipe',
  TAP = 'tap'
}

@Component({
  selector: 'app-snake-game',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="game-modal" [class.show]="showGame()" [class.fullscreen]="isFullscreen()" #gameModal>
      <div class="game-container" [class.fullscreen]="isFullscreen()">
        <div class="game-header">
          <h3>🐍 Snake Game</h3>
          <div class="game-info">
            <div class="score">Puntuación: {{ score() }}</div>
            <div class="high-score">Mejor: {{ highScore() }}</div>
            <div class="lives">❤️ Vidas: {{ lives() }}</div>
            <div class="game-time">⏱️ Tiempo: {{ gameTime() }}s</div>
            <div class="speed-level">🚀 Nivel: {{ speedLevel() }}</div>
            @if (speedBoostActive()) {
              <div class="power-up-status">⚡ Turbo: {{ speedBoostTimeLeft() }}s</div>
            }
          </div>
          <div class="header-buttons">
            <button class="fullscreen-btn"
                    (click)="toggleFullscreen()"
                    [title]="isFullscreen() ? 'Salir de pantalla completa' : 'Pantalla completa'">
              {{ isFullscreen() ? '🗗' : '🗖' }}
            </button>
            <button class="close-btn" (click)="closeGame()" title="Cerrar juego">
              ✕
            </button>
          </div>
        </div>
        
        <div class="game-board">
          <canvas #gameCanvas
                  [width]="canvasSize().width"
                  [height]="canvasSize().height" 
                  tabindex="0"
                  (keydown)="handleKeyPress($event)"
                  (touchstart)="handleTouchStart($event)"
                  (touchend)="handleTouchEnd($event)"
                  [style.border]="gameOver() ? '2px solid var(--danger-color)' : '2px solid var(--border-color)'"
                  [style.touch-action]="'none'">
          </canvas>
          
          @if (gameOver()) {
            <div class="game-over-overlay">
              <div class="game-over-message">
                <h4>¡Game Over!</h4>
                <p>Puntuación Final: {{ score() }}</p>
                @if (isNewHighScore()) {
                  <p class="new-record">🎉 ¡Nuevo Récord!</p>
                }
                <button class="restart-btn" (click)="resetGame()">
                  <i class="fas fa-redo"></i>
                  Jugar de Nuevo
                </button>
              </div>
            </div>
          }
        </div>
        
        <!-- Control Mode Toggle -->
        <div class="control-mode-toggle">
          <span class="toggle-label">Controles:</span>
          <button 
            class="mode-btn" 
            [class.active]="controlMode() === 'swipe'"
            (click)="setControlMode('swipe')">
            👆 Deslizar
          </button>
          <button 
            class="mode-btn" 
            [class.active]="controlMode() === 'tap'"
            (click)="setControlMode('tap')">
            👇 Tocar
          </button>
        </div>

        <div class="game-controls">
          <button 
            class="control-btn play-pause" 
            (click)="toggleGame()"
            [disabled]="gameOver()">
            <i class="fas" [class.fa-play]="!gameRunning()" [class.fa-pause]="gameRunning()"></i>
            {{ gameRunning() ? 'Pausar' : (gameStarted() ? 'Continuar' : 'Jugar') }}
          </button>
          
          <button class="control-btn reset" (click)="resetGame()">
            <i class="fas fa-redo"></i>
            Reiniciar
          </button>
        </div>
        
        <!-- Exit Button -->
        <div class="exit-controls">
          <button class="control-btn exit-btn" (click)="exitGame()">
            <i class="fas fa-sign-out-alt"></i>
            Salir del Juego
          </button>
        </div>
        
        <div class="game-instructions">
          <p><strong>🎮 Controles Táctiles:</strong></p>
          @if (controlMode() === 'swipe') {
            <p>👆 <strong>Desliza</strong> hacia arriba, abajo, izquierda o derecha en el canvas</p>
          } @else {
            <p>👇 <strong>Toca</strong> en la dirección que quieres que vaya la serpiente en el canvas</p>
          }
          <p>🐍 La serpiente <strong>inicia automáticamente</strong> moviéndose hacia la derecha</p>
          <p>🎯 <strong>Objetivo:</strong> Come los cuadraditos rojos para crecer y ganar puntos!</p>
        </div>
      </div>
    </div>
  `,
  styleUrls: ['./snake-game.component.scss']
})
export class SnakeGameComponent {
  @ViewChild('gameCanvas', { static: true }) canvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('gameModal', { static: true }) gameModal!: ElementRef<HTMLDivElement>;
  private ctx!: CanvasRenderingContext2D;
  private gameInterval?: number;
  
  // Game constants
  private readonly DEFAULT_CANVAS_SIZE = 400;
  private readonly GRID_SIZE = 20;
  private readonly INITIAL_SPEED = 200; // ms between moves
  private readonly POWER_UP_DURATION = 8000; // 8 seconds
  private readonly POWER_UP_SPAWN_CHANCE = 0.15; // 15% chance after eating food
  private readonly POWER_UP_LIFETIME = 10000; // 10 seconds before power-up expires
  private readonly POWER_UP_WARNING_TIME = 3000; // Start warning 3 seconds before expiry
  private readonly SPEED_INCREASE_INTERVAL = 10000; // 10 seconds
  private readonly MIN_SPEED = 80; // Minimum speed limit
  
  // Game state signals
  showGame = signal(false);
  score = signal(0);
  highScore = signal(this.loadHighScore());
  gameRunning = signal(false);
  gameStarted = signal(false);
  gameOver = signal(false);
  isNewHighScore = signal(false);
  lives = signal(1);
  isFullscreen = signal(false);
  
  // Power-up system
  powerUp = signal<PowerUp | null>(null);
  speedBoostActive = signal(false);
  speedBoostTimeLeft = signal(0);
  private speedBoostInterval?: number;
  private powerUpExpiryInterval?: number;
  
  // Time-based speed system
  gameTime = signal(0); // seconds
  speedLevel = signal(1);
  private gameTimer?: number;
  private gameStartTime = 0;
  
  // Control system
  controlMode = signal<ControlMode>(ControlMode.SWIPE);
  
  // Snake and food
  snake = signal<Position[]>([{ x: 200, y: 200 }]);
  food = signal<Position>({ x: 100, y: 100 });
  direction = signal<Direction>({ x: 0, y: 0 });
  
  // Computed values
  snakeLength = computed(() => this.snake().length);
  canvasSize = computed(() => {
    if (this.isFullscreen()) {
      // Calculate optimal fullscreen size based on screen dimensions
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight - 100; // Leave space for controls
      const maxSize = Math.min(screenWidth - 40, screenHeight - 40);

      // Round to nearest grid multiple to avoid rendering issues
      const size = Math.floor(maxSize / this.GRID_SIZE) * this.GRID_SIZE;
      return { width: Math.max(size, 400), height: Math.max(size, 400) };
    }
    return { width: this.DEFAULT_CANVAS_SIZE, height: this.DEFAULT_CANVAS_SIZE };
  });

  gameSpeed = computed(() => {
    // Base speed from score progression
    let scoreSpeedReduction = Math.floor(this.score() / 50) * 10;

    // Time-based speed increase (every 10 seconds, reduce delay by 15ms)
    let timeSpeedReduction = (this.speedLevel() - 1) * 15;

    // Calculate base speed with both factors
    let baseSpeed = Math.max(this.MIN_SPEED, this.INITIAL_SPEED - scoreSpeedReduction - timeSpeedReduction);

    // Apply 2X speed boost if active
    return this.speedBoostActive() ? Math.max(this.MIN_SPEED / 2, Math.floor(baseSpeed / 2)) : baseSpeed;
  });
  
  constructor() {
    // Effect to update high score when score changes
    effect(() => {
      const currentScore = this.score();
      const currentHighScore = this.highScore();

      if (currentScore > currentHighScore) {
        this.highScore.set(currentScore);
        this.isNewHighScore.set(true);
        this.saveHighScore(currentScore);
      }
    });

    // Listen for fullscreen changes
    document.addEventListener('fullscreenchange', () => {
      this.isFullscreen.set(!!document.fullscreenElement);
      this.updateCanvasSize();
    });
  }
  
  ngAfterViewInit() {
    this.ctx = this.canvas.nativeElement.getContext('2d')!;
    this.initializeGame();
  }
  
  ngOnDestroy() {
    if (this.gameInterval) {
      clearInterval(this.gameInterval);
    }
    if (this.gameTimer) {
      clearInterval(this.gameTimer);
    }
    if (this.speedBoostInterval) {
      clearInterval(this.speedBoostInterval);
    }
    if (this.powerUpExpiryInterval) {
      clearInterval(this.powerUpExpiryInterval);
    }

    // Exit fullscreen if active
    if (document.fullscreenElement) {
      document.exitFullscreen();
    }
  }
  
  // Touch control variables
  private touchStartX = 0;
  private touchStartY = 0;
  private readonly MIN_SWIPE_DISTANCE = 50;

  // Check if device is mobile
  isMobile(): boolean {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  // Public methods
  openGame() {
    // Only allow on mobile devices
    if (!this.isMobile()) {
      alert('🎮 Snake Game está optimizado solo para dispositivos móviles. Usa un teléfono o tablet para jugar.');
      return;
    }
    
    this.showGame.set(true);
    this.canvas.nativeElement.focus();
    
    // Auto-start the game with initial direction
    setTimeout(() => {
      this.autoStartGame();
    }, 500);
  }
  
  closeGame() {
    this.pauseGame();
    this.showGame.set(false);

    // Exit fullscreen if active
    if (document.fullscreenElement) {
      document.exitFullscreen();
    }
  }

  // Fullscreen methods
  toggleFullscreen() {
    if (!document.fullscreenElement) {
      this.gameModal.nativeElement.requestFullscreen().catch(err => {
        console.error(`Error attempting to enable fullscreen: ${err.message}`);
      });
    } else {
      document.exitFullscreen();
    }
  }

  updateCanvasSize() {
    // Force canvas size update
    setTimeout(() => {
      this.resetGameBoard();
      this.drawGame();
    }, 100);
  }

  exitGame() {
    // Pause the game first
    this.pauseGame();
    
    // Show confirmation if game is in progress
    if (this.gameStarted() && !this.gameOver()) {
      const confirmExit = confirm('¿Estás seguro que quieres salir? Se perderá el progreso del juego actual.');
      if (!confirmExit) {
        return;
      }
    }
    
    // Close the game
    this.closeGame();
  }
  
  toggleGame() {
    if (this.gameOver()) return;
    
    if (this.gameRunning()) {
      this.pauseGame();
    } else {
      this.startGame();
    }
  }
  
  startGame() {
    if (this.gameOver()) {
      this.resetGame();
      return;
    }
    
    this.gameRunning.set(true);
    this.gameStarted.set(true);
    
    // Start game timer
    if (!this.gameStartTime) {
      this.gameStartTime = Date.now();
      this.startGameTimer();
    }
    
    if (this.gameInterval) {
      clearInterval(this.gameInterval);
    }
    
    this.gameInterval = window.setInterval(() => {
      this.updateGame();
      this.drawGame();
    }, this.gameSpeed());
  }
  
  pauseGame() {
    this.gameRunning.set(false);
    if (this.gameInterval) {
      clearInterval(this.gameInterval);
      this.gameInterval = undefined;
    }
    // Don't stop the timer on pause, just the game loop
  }

  startGameTimer() {
    this.gameTimer = window.setInterval(() => {
      if (this.gameRunning() && !this.gameOver()) {
        const currentTime = Math.floor((Date.now() - this.gameStartTime) / 1000);
        this.gameTime.set(currentTime);
        
        // Check if we should increase speed level
        const newSpeedLevel = Math.floor(currentTime / (this.SPEED_INCREASE_INTERVAL / 1000)) + 1;
        if (newSpeedLevel > this.speedLevel()) {
          this.speedLevel.set(newSpeedLevel);
          this.updateGameSpeed();
          
          console.log(`🚀 Speed Level increased to ${newSpeedLevel}! Time: ${currentTime}s`);
          
          // Optional: Show visual feedback for speed increase
          this.showSpeedLevelUp();
        }
      }
    }, 1000); // Update every second
  }

  showSpeedLevelUp() {
    // Simple console log for now, could add visual effect later
    console.log('⚡ SPEED LEVEL UP! ⚡');
  }

  // Control mode methods
  setControlMode(mode: 'swipe' | 'tap') {
    this.controlMode.set(mode as ControlMode);
    console.log('🎮 Control mode changed to:', mode);
  }
  
  resetGame() {
    this.pauseGame();
    
    // Clear all timers
    if (this.gameTimer) {
      clearInterval(this.gameTimer);
      this.gameTimer = undefined;
    }
    
    // Reset all game state
    this.score.set(0);
    this.gameOver.set(false);
    this.gameStarted.set(false);
    this.isNewHighScore.set(false);
    this.lives.set(1);
    this.gameTime.set(0);
    this.speedLevel.set(1);
    this.gameStartTime = 0;
    this.resetGameBoard();
    this.clearPowerUps();
    this.generateFood();
    this.drawGame();
    this.canvas.nativeElement.focus();

    // Auto-start after reset
    setTimeout(() => {
      this.autoStartGame();
    }, 500);
  }

  resetGameBoard() {
    const size = this.canvasSize();
    const centerX = Math.floor((size.width / 2 / this.GRID_SIZE)) * this.GRID_SIZE;
    const centerY = Math.floor((size.height / 2 / this.GRID_SIZE)) * this.GRID_SIZE;

    this.snake.set([{ x: centerX, y: centerY }]);
    this.direction.set({ x: 0, y: 0 });
  }

  autoStartGame() {
    // Set initial direction (moving right)
    this.direction.set({ x: 1, y: 0 });
    this.startGame();
  }

  // Power-up methods
  clearPowerUps() {
    this.powerUp.set(null);
    this.speedBoostActive.set(false);
    this.speedBoostTimeLeft.set(0);
    if (this.speedBoostInterval) {
      clearInterval(this.speedBoostInterval);
      this.speedBoostInterval = undefined;
    }
    if (this.powerUpExpiryInterval) {
      clearInterval(this.powerUpExpiryInterval);
      this.powerUpExpiryInterval = undefined;
    }
  }

  generatePowerUp() {
    if (this.powerUp() || Math.random() > this.POWER_UP_SPAWN_CHANCE) {
      return;
    }

    let newPowerUp: PowerUp;
    const currentSnake = this.snake();
    const currentFood = this.food();

    const size = this.canvasSize();

    do {
      newPowerUp = {
        x: Math.floor(Math.random() * (size.width / this.GRID_SIZE)) * this.GRID_SIZE,
        y: Math.floor(Math.random() * (size.height / this.GRID_SIZE)) * this.GRID_SIZE,
        type: 'speed_boost',
        active: true,
        spawnTime: Date.now(),
        timeLeft: this.POWER_UP_LIFETIME
      };
    } while (
      currentSnake.some(segment => segment.x === newPowerUp.x && segment.y === newPowerUp.y) ||
      (currentFood.x === newPowerUp.x && currentFood.y === newPowerUp.y)
    );

    this.powerUp.set(newPowerUp);
    console.log('🔥 Power-up spawned at:', newPowerUp.x, newPowerUp.y);

    // Start expiry timer
    this.startPowerUpExpiryTimer();
  }

  startPowerUpExpiryTimer() {
    if (this.powerUpExpiryInterval) {
      clearInterval(this.powerUpExpiryInterval);
    }

    this.powerUpExpiryInterval = window.setInterval(() => {
      const currentPowerUp = this.powerUp();
      if (!currentPowerUp) {
        return;
      }

      const elapsed = Date.now() - currentPowerUp.spawnTime;
      const timeLeft = this.POWER_UP_LIFETIME - elapsed;

      if (timeLeft <= 0) {
        // Power-up expired
        console.log('⏰ Power-up expired!');
        this.expirePowerUp();
      } else {
        // Update time left for visual feedback
        currentPowerUp.timeLeft = timeLeft;
        this.powerUp.set({ ...currentPowerUp });
      }
    }, 100); // Update every 100ms for smooth animation
  }

  expirePowerUp() {
    const expiredPowerUp = this.powerUp();
    console.log('💨 Power-up disappeared!');

    // Show visual feedback for expiry
    if (expiredPowerUp) {
      this.showPowerUpExpiredEffect(expiredPowerUp.x, expiredPowerUp.y);
    }

    this.powerUp.set(null);
    if (this.powerUpExpiryInterval) {
      clearInterval(this.powerUpExpiryInterval);
      this.powerUpExpiryInterval = undefined;
    }
  }

  showPowerUpExpiredEffect(x: number, y: number) {
    // Create a temporary visual effect
    const canvas = this.canvas.nativeElement;
    const ctx = canvas.getContext('2d')!;

    // Store original state
    const originalComposite = ctx.globalCompositeOperation;

    // Draw disappearing effect
    for (let i = 0; i < 5; i++) {
      setTimeout(() => {
        ctx.save();
        ctx.globalAlpha = 0.7 - (i * 0.15);
        ctx.fillStyle = '#666666';

        // Draw fading particles
        const particleSize = 4 - i;
        const spread = i * 8;

        ctx.fillRect(x + this.GRID_SIZE/2 - spread - particleSize, y + this.GRID_SIZE/2 - particleSize, particleSize * 2, particleSize * 2);
        ctx.fillRect(x + this.GRID_SIZE/2 + spread - particleSize, y + this.GRID_SIZE/2 - particleSize, particleSize * 2, particleSize * 2);
        ctx.fillRect(x + this.GRID_SIZE/2 - particleSize, y + this.GRID_SIZE/2 - spread - particleSize, particleSize * 2, particleSize * 2);
        ctx.fillRect(x + this.GRID_SIZE/2 - particleSize, y + this.GRID_SIZE/2 + spread - particleSize, particleSize * 2, particleSize * 2);

        ctx.restore();

        // Redraw the game after effect
        if (i === 4) {
          setTimeout(() => this.drawGame(), 50);
        }
      }, i * 100);
    }
  }

  activateSpeedBoost() {
    // Clear expiry timer since power-up was collected
    if (this.powerUpExpiryInterval) {
      clearInterval(this.powerUpExpiryInterval);
      this.powerUpExpiryInterval = undefined;
    }

    this.speedBoostActive.set(true);
    this.speedBoostTimeLeft.set(Math.ceil(this.POWER_UP_DURATION / 1000));

    // Add a life for surviving the challenge
    this.lives.update(lives => lives + 1);

    console.log('⚡ Speed boost activated! Lives:', this.lives());

    // Update game speed immediately
    this.updateGameSpeed();

    // Start countdown timer
    this.speedBoostInterval = window.setInterval(() => {
      const timeLeft = this.speedBoostTimeLeft();
      if (timeLeft > 1) {
        this.speedBoostTimeLeft.set(timeLeft - 1);
      } else {
        // Deactivate speed boost
        this.deactivateSpeedBoost();
      }
    }, 1000);
  }

  deactivateSpeedBoost() {
    this.speedBoostActive.set(false);
    this.speedBoostTimeLeft.set(0);
    if (this.speedBoostInterval) {
      clearInterval(this.speedBoostInterval);
      this.speedBoostInterval = undefined;
    }
    
    // Reset to normal speed
    this.updateGameSpeed();
    console.log('⚡ Speed boost deactivated');
  }

  // Life system method
  loseLife() {
    const currentLives = this.lives();
    if (currentLives > 1) {
      this.lives.set(currentLives - 1);
      this.respawn();
      return true; // Continue game
    } else {
      return false; // Game over
    }
  }

  respawn() {
    console.log('💖 Respawning! Lives left:', this.lives());
    
    const size = this.canvasSize();

    // Find safe spawn position (away from edges)
    const safePositions = [
      { x: 60, y: 60 },
      { x: size.width - 80, y: 60 },
      { x: 60, y: size.height - 80 },
      { x: size.width - 80, y: size.height - 80 },
      { x: Math.floor(size.width / 2), y: Math.floor(size.height / 2) } // Center
    ];
    
    const spawnPosition = safePositions[Math.floor(Math.random() * safePositions.length)];
    
    // Reset snake to small size at spawn position
    this.snake.set([spawnPosition]);
    this.direction.set({ x: 1, y: 0 }); // Moving right
    
    // Clear any power-ups to give player a fresh start
    this.clearPowerUps();
    
    // Brief invulnerability period
    setTimeout(() => {
      console.log('🛡️ Invulnerability ended');
    }, 1000);
  }
  
  changeDirection(dir: 'up' | 'down' | 'left' | 'right') {
    if (!this.gameRunning()) return;
    
    const currentDir = this.direction();
    
    switch(dir) {
      case 'up':
        if (currentDir.y === 0) this.direction.set({ x: 0, y: -1 });
        break;
      case 'down':
        if (currentDir.y === 0) this.direction.set({ x: 0, y: 1 });
        break;
      case 'left':
        if (currentDir.x === 0) this.direction.set({ x: -1, y: 0 });
        break;
      case 'right':
        if (currentDir.x === 0) this.direction.set({ x: 1, y: 0 });
        break;
    }
  }
  
  handleKeyPress(event: KeyboardEvent) {
    event.preventDefault();
    
    // Game controls
    if (event.key === ' ') {
      this.toggleGame();
      return;
    }
    
    if (event.key === 'r' || event.key === 'R') {
      this.resetGame();
      return;
    }
    
    // Direction controls
    switch(event.key) {
      case 'ArrowUp':
        this.changeDirection('up');
        break;
      case 'ArrowDown':
        this.changeDirection('down');
        break;
      case 'ArrowLeft':
        this.changeDirection('left');
        break;
      case 'ArrowRight':
        this.changeDirection('right');
        break;
    }
  }

  // Touch control methods
  handleTouchStart(event: TouchEvent) {
    event.preventDefault();
    if (event.touches.length > 0) {
      this.touchStartX = event.touches[0].clientX;
      this.touchStartY = event.touches[0].clientY;
    }
  }

  handleTouchEnd(event: TouchEvent) {
    event.preventDefault();
    if (event.changedTouches.length === 0) return;

    const touchEndX = event.changedTouches[0].clientX;
    const touchEndY = event.changedTouches[0].clientY;

    if (this.controlMode() === ControlMode.SWIPE) {
      // Swipe mode: Calculate swipe direction
      const deltaX = touchEndX - this.touchStartX;
      const deltaY = touchEndY - this.touchStartY;
      
      // Check if swipe distance is sufficient
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      if (distance < this.MIN_SWIPE_DISTANCE) return;
      
      // Determine swipe direction
      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        // Horizontal swipe
        if (deltaX > 0) {
          this.changeDirection('right');
        } else {
          this.changeDirection('left');
        }
      } else {
        // Vertical swipe
        if (deltaY > 0) {
          this.changeDirection('down');
        } else {
          this.changeDirection('up');
        }
      }
    } else {
      // Tap mode: Calculate tap direction based on snake head position
      this.handleTapDirection(touchEndX, touchEndY);
    }
  }

  handleTapDirection(touchX: number, touchY: number) {
    // Get canvas bounds
    const canvas = this.canvas.nativeElement;
    const rect = canvas.getBoundingClientRect();
    
    // Convert screen coordinates to canvas coordinates
    const size = this.canvasSize();
    const canvasX = ((touchX - rect.left) / rect.width) * size.width;
    const canvasY = ((touchY - rect.top) / rect.height) * size.height;
    
    // Get snake head position
    const head = this.snake()[0];
    const headCenterX = head.x + this.GRID_SIZE / 2;
    const headCenterY = head.y + this.GRID_SIZE / 2;
    
    // Calculate direction from head to tap point
    const deltaX = canvasX - headCenterX;
    const deltaY = canvasY - headCenterY;
    
    // Determine primary direction (prioritize larger delta)
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      // Horizontal movement
      if (deltaX > 0) {
        this.changeDirection('right');
      } else {
        this.changeDirection('left');
      }
    } else {
      // Vertical movement  
      if (deltaY > 0) {
        this.changeDirection('down');
      } else {
        this.changeDirection('up');
      }
    }
    
    console.log('🎯 Tap direction:', { 
      tapX: canvasX, 
      tapY: canvasY, 
      headX: headCenterX, 
      headY: headCenterY,
      deltaX,
      deltaY 
    });
  }
  
  // Private game methods
  private initializeGame() {
    this.generateFood();
    this.drawGame();
  }
  
  private updateGame() {
    const currentSnake = [...this.snake()];
    const head = { ...currentSnake[0] };
    const dir = this.direction();
    
    // No movement if no direction is set
    if (dir.x === 0 && dir.y === 0) return;
    
    // Move head
    head.x += dir.x * this.GRID_SIZE;
    head.y += dir.y * this.GRID_SIZE;
    
    const size = this.canvasSize();

    // Check wall collision
    if (head.x < 0 || head.x >= size.width ||
        head.y < 0 || head.y >= size.height) {
      if (!this.loseLife()) {
        this.endGame();
      }
      return;
    }
    
    // Check self collision
    if (currentSnake.some(segment => segment.x === head.x && segment.y === head.y)) {
      if (!this.loseLife()) {
        this.endGame();
      }
      return;
    }
    
    // Add new head
    const newSnake = [head, ...currentSnake];
    
    // Check power-up collision
    const currentPowerUp = this.powerUp();
    if (currentPowerUp && head.x === currentPowerUp.x && head.y === currentPowerUp.y) {
      console.log('🔥 Power-up collected!');
      this.powerUp.set(null); // Remove power-up
      this.activateSpeedBoost();
      this.score.update(s => s + 50); // Bonus points for power-up
    }
    
    // Check food collision
    const currentFood = this.food();
    if (head.x === currentFood.x && head.y === currentFood.y) {
      // Snake grows (don't remove tail)
      this.score.update(s => s + 10);
      this.generateFood();
      
      // Try to generate power-up after eating food
      this.generatePowerUp();
      
      // Increase speed slightly every 5 foods
      if (this.score() % 50 === 0) {
        this.updateGameSpeed();
      }
    } else {
      // Remove tail (snake doesn't grow)
      newSnake.pop();
    }
    
    this.snake.set(newSnake);
  }
  
  private updateGameSpeed() {
    if (this.gameInterval) {
      clearInterval(this.gameInterval);
      this.gameInterval = window.setInterval(() => {
        this.updateGame();
        this.drawGame();
      }, this.gameSpeed());
    }
  }
  
  private endGame() {
    this.pauseGame();
    this.gameOver.set(true);
  }
  
  private generateFood() {
    let newFood: Position;
    const currentSnake = this.snake();
    const size = this.canvasSize();

    do {
      newFood = {
        x: Math.floor(Math.random() * (size.width / this.GRID_SIZE)) * this.GRID_SIZE,
        y: Math.floor(Math.random() * (size.height / this.GRID_SIZE)) * this.GRID_SIZE
      };
    } while (currentSnake.some(segment => segment.x === newFood.x && segment.y === newFood.y));

    this.food.set(newFood);
  }
  
  private drawGame() {
    if (!this.ctx) return;

    const ctx = this.ctx;
    const size = this.canvasSize();

    // Clear canvas
    ctx.fillStyle = getComputedStyle(document.documentElement).getPropertyValue('--bg-secondary') || '#111827';
    ctx.fillRect(0, 0, size.width, size.height);

    // Draw grid (subtle)
    ctx.strokeStyle = getComputedStyle(document.documentElement).getPropertyValue('--border-color') || '#374151';
    ctx.lineWidth = 0.5;
    for (let i = 0; i <= size.width; i += this.GRID_SIZE) {
      ctx.beginPath();
      ctx.moveTo(i, 0);
      ctx.lineTo(i, size.height);
      ctx.stroke();
    }
    for (let i = 0; i <= size.height; i += this.GRID_SIZE) {
      ctx.beginPath();
      ctx.moveTo(0, i);
      ctx.lineTo(size.width, i);
      ctx.stroke();
    }
    
    // Draw snake
    const currentSnake = this.snake();
    const isSpeedBoostActive = this.speedBoostActive();
    
    currentSnake.forEach((segment, index) => {
      if (index === 0) {
        // Head - change color based on power-up state
        ctx.fillStyle = isSpeedBoostActive ? '#F59E0B' : '#10B981'; // Orange when boosted, green normally
        ctx.fillRect(segment.x + 1, segment.y + 1, this.GRID_SIZE - 2, this.GRID_SIZE - 2);
        
        // Eyes
        ctx.fillStyle = '#000000';
        const eyeSize = 3;
        const eyeOffset = 5;
        ctx.fillRect(segment.x + eyeOffset, segment.y + eyeOffset, eyeSize, eyeSize);
        ctx.fillRect(segment.x + this.GRID_SIZE - eyeOffset - eyeSize, segment.y + eyeOffset, eyeSize, eyeSize);
        
        // Glow effect when speed boost is active
        if (isSpeedBoostActive) {
          ctx.shadowColor = '#F59E0B';
          ctx.shadowBlur = 10;
          ctx.fillRect(segment.x + 1, segment.y + 1, this.GRID_SIZE - 2, this.GRID_SIZE - 2);
          ctx.shadowBlur = 0;
        }
      } else {
        // Body - change color based on power-up state
        const opacity = 1 - (index * 0.05);
        const baseColor = isSpeedBoostActive ? '245, 158, 11' : '34, 197, 94'; // Orange or green
        ctx.fillStyle = `rgba(${baseColor}, ${Math.max(opacity, 0.3)})`;
        ctx.fillRect(segment.x + 2, segment.y + 2, this.GRID_SIZE - 4, this.GRID_SIZE - 4);
      }
    });
    
    // Draw food (animated)
    const currentFood = this.food();
    const time = Date.now();
    const pulse = Math.sin(time * 0.01) * 0.2 + 0.8; // Pulsing effect
    
    ctx.fillStyle = '#EF4444';
    const foodSize = (this.GRID_SIZE - 4) * pulse;
    const offset = (this.GRID_SIZE - foodSize) / 2;
    ctx.fillRect(currentFood.x + offset, currentFood.y + offset, foodSize, foodSize);
    
    // Food highlight
    ctx.fillStyle = '#FCA5A5';
    const highlightSize = foodSize * 0.6;
    const highlightOffset = (this.GRID_SIZE - highlightSize) / 2;
    ctx.fillRect(currentFood.x + highlightOffset, currentFood.y + highlightOffset, highlightSize, highlightSize);
    
    // Draw power-up (if active)
    const currentPowerUp = this.powerUp();
    if (currentPowerUp) {
      const powerUpTime = Date.now();
      const isExpiring = currentPowerUp.timeLeft < this.POWER_UP_WARNING_TIME;

      // Faster pulsing when about to expire
      const pulseSpeed = isExpiring ? 0.02 : 0.008;
      const powerUpPulse = Math.sin(powerUpTime * pulseSpeed) * 0.3 + 0.7;

      // Change color when expiring
      const baseColor = isExpiring ? '#EF4444' : '#F59E0B'; // Red when expiring
      const glowColor = isExpiring ? '#FCA5A5' : '#FCD34D';

      // Blinking effect when very close to expiry
      const isVeryClose = currentPowerUp.timeLeft < 1000; // Last second
      const shouldBlink = isVeryClose && Math.sin(powerUpTime * 0.05) > 0;

      if (!shouldBlink || !isVeryClose) {
        // Power-up base (lightning bolt effect)
        ctx.fillStyle = baseColor;
        const powerUpSize = (this.GRID_SIZE - 2) * powerUpPulse;
        const powerUpOffset = (this.GRID_SIZE - powerUpSize) / 2;

        // Draw diamond shape for power-up
        ctx.save();
        ctx.translate(currentPowerUp.x + this.GRID_SIZE / 2, currentPowerUp.y + this.GRID_SIZE / 2);
        ctx.rotate(powerUpTime * 0.003); // Slow rotation
        ctx.fillRect(-powerUpSize / 2, -powerUpSize / 2, powerUpSize, powerUpSize);

        // Inner glow
        ctx.fillStyle = glowColor;
        const innerSize = powerUpSize * 0.6;
        ctx.fillRect(-innerSize / 2, -innerSize / 2, innerSize, innerSize);

        // Lightning effect
        ctx.strokeStyle = isExpiring ? '#FBBF24' : '#FBBF24';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(-innerSize / 3, -innerSize / 3);
        ctx.lineTo(0, 0);
        ctx.lineTo(innerSize / 3, innerSize / 3);
        ctx.stroke();

        ctx.restore();

        // Outer glow effect - more intense when expiring
        const glowIntensity = isExpiring ? 25 : 15;
        const glowAlpha = isExpiring ? 0.8 : 0.5;

        ctx.shadowColor = baseColor;
        ctx.shadowBlur = glowIntensity;
        ctx.globalAlpha = glowAlpha;
        ctx.fillStyle = baseColor;
        ctx.fillRect(currentPowerUp.x + 2, currentPowerUp.y + 2, this.GRID_SIZE - 4, this.GRID_SIZE - 4);
        ctx.shadowBlur = 0;
        ctx.globalAlpha = 1;
      }
    }
  }
  
  // High score persistence
  private loadHighScore(): number {
    const saved = localStorage.getItem('snake-high-score');
    return saved ? parseInt(saved, 10) : 0;
  }
  
  private saveHighScore(score: number): void {
    localStorage.setItem('snake-high-score', score.toString());
  }
}